<template>
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div
            class="flex flex-col gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-gray-800"
        >
            <div
                class="text-[13px] font-medium text-zinc-500 dark:text-gray-400"
            >
                Processados hoje
            </div>
            <div class="flex items-baseline gap-2">
                <span
                    class="text-[26px] font-semibold tabular-nums tracking-tight text-zinc-950 dark:text-white"
                >
                    {{ stats.today.processed }}
                </span>
                <span class="text-[13px] text-zinc-500 dark:text-gray-400">
                    de {{ goal }}
                </span>
            </div>
            <div
                class="h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-gray-700"
            >
                <div
                    class="h-full rounded-full bg-zinc-900 transition-all duration-500 dark:bg-white"
                    :style="{ width: progressPercentage + '%' }"
                ></div>
            </div>
        </div>

        <button
            type="button"
            class="flex flex-col gap-2 rounded-xl border bg-white px-4 py-3.5 text-left transition-colors hover:bg-zinc-50 dark:bg-gray-800 dark:hover:bg-gray-700/60"
            :class="
                urgentActive
                    ? 'border-zinc-900 ring-1 ring-zinc-900 dark:border-white dark:ring-white'
                    : 'border-zinc-200 dark:border-gray-700'
            "
            v-tooltip="
                urgentActive
                    ? 'Mostrar a fila completa'
                    : 'Filtrar a fila pelos saques que vencem até 12h'
            "
            @click="$emit('toggle-urgent')"
        >
            <div class="flex items-center justify-between">
                <span
                    class="text-[13px] font-medium text-zinc-500 dark:text-gray-400"
                >
                    Pendentes até 12h
                </span>
                <span
                    v-if="urgentActive"
                    class="rounded-full bg-zinc-900 px-2 py-px text-xs font-medium text-white dark:bg-white dark:text-gray-900"
                >
                    Filtrando
                </span>
            </div>
            <div class="flex items-baseline gap-2">
                <span
                    class="text-[26px] font-semibold tabular-nums tracking-tight text-zinc-950 dark:text-white"
                >
                    {{ urgentCount }}
                </span>
                <span class="text-[13px] text-zinc-500 dark:text-gray-400">
                    {{ highRiskCount }} de alto risco na fila
                </span>
            </div>
        </button>

        <div
            class="flex flex-col gap-2 rounded-xl border bg-white px-4 py-3.5 dark:bg-gray-800"
            :class="
                pace.late
                    ? 'border-red-200 dark:border-red-500/30'
                    : 'border-zinc-200 dark:border-gray-700'
            "
        >
            <div class="flex items-center justify-between">
                <span
                    class="text-[13px] font-medium text-zinc-500 dark:text-gray-400"
                >
                    Prazo 12h
                </span>
                <span
                    class="rounded-full px-2 py-px text-xs font-medium"
                    :class="
                        pace.late
                            ? 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400'
                            : 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                    "
                >
                    {{ pace.label }}
                </span>
            </div>
            <div class="flex flex-col gap-0.5">
                <span
                    class="font-mono text-2xl font-medium tabular-nums text-zinc-950 dark:text-white"
                >
                    {{ remainingTimeFormatted }}
                </span>
                <span class="text-[12.5px] text-zinc-500 dark:text-gray-400">
                    {{ pace.text }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";

/** Saques por minuto acima do qual o ritmo atual não fecha a fila até 12h. */
const LATE_PACE_PER_MINUTE = 1.5;

export default {
    name: "OperatorStats",
    props: {
        urgentCount: {
            type: Number,
            default: 0,
        },
        highRiskCount: {
            type: Number,
            default: 0,
        },
        remainingTime: {
            type: Number,
            default: 0,
        },
        remainingTimeFormatted: {
            type: String,
            default: "00:00:00",
        },
        midDayPassed: {
            type: Boolean,
            default: false,
        },
        urgentActive: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["toggle-urgent"],
    data() {
        return {
            stats: {
                today: {
                    processed: 0,
                    approved: 0,
                    rejected: 0,
                },
            },
        };
    },
    computed: {
        goal() {
            return this.stats.today.processed + this.urgentCount;
        },
        progressPercentage() {
            if (this.goal === 0) {
                return 100;
            }

            return Math.round((this.stats.today.processed / this.goal) * 100);
        },
        pace() {
            if (this.urgentCount === 0) {
                return {
                    late: false,
                    label: "Tudo em dia",
                    text: "Nenhum saque pendente até 12h",
                };
            }

            if (this.midDayPassed) {
                return {
                    late: true,
                    label: "Prazo excedido",
                    text: `${this.urgentCount} pendentes após o prazo`,
                };
            }

            const minutes = this.remainingTime / 60;
            const rate = minutes > 0 ? this.urgentCount / minutes : Infinity;
            const late = rate > LATE_PACE_PER_MINUTE;

            return {
                late,
                label: late ? "Acelerar ritmo" : "No ritmo",
                text: `${rate.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} saques/min necessários`,
            };
        },
    },
    mounted() {
        this.fetchStats();
    },
    methods: {
        async fetchStats() {
            try {
                const { data } = await axios.get(
                    "/api/cashout/review/operator-stats",
                );
                this.stats = { ...this.stats, ...data };
            } catch (error) {
                console.error("Erro ao buscar estatísticas:", error);
            }
        },
    },
};
</script>
